import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useTheme,
} from "@mui/material";

import CoreInput from "../../core/CoreInput";
import { chatService } from "../../../../services";
import type { ApiResponse } from "../../../../services/apiService";
import { API_CONFIG } from "../../../../services/apiConfig";
import useNotifier from "../../../../hooks/useNotifier";

function renameFailureMessage(response: ApiResponse): string {
  const detail = response.error?.trim();
  if (response.status === API_CONFIG.STATUS_CODES.CONFLICT) {
    return detail || "This chat is closed and cannot be renamed.";
  }
  if (response.status === API_CONFIG.STATUS_CODES.FORBIDDEN) {
    return detail || "You do not have permission to rename this chat.";
  }
  return detail || "Failed to update the chat title. Please try again.";
}

interface RenameChatDialogProps {
  isOpen: boolean;
  title: string;
  chatId: string;
  onClose: () => void;
  onConfirmCallback: (newTitle: string) => void;
}

const RenameChatDialog: React.FC<RenameChatDialogProps> = ({
  isOpen,
  title,
  chatId,
  onClose,
  onConfirmCallback,
}) => {
  const notifier = useNotifier();
  const theme = useTheme();

  const [inputValue, setInputValue] = useState("");
  const [isOpInProgress, setIsOpInProgress] = useState(false);

  useEffect(() => {
    if (isOpen && title?.trim()) {
      setInputValue(title);
    }
  }, [title, isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      handleConfirm();
    }
  };

  const handleClose = () => {
    onClose();
    setInputValue("");
    setIsOpInProgress(false);
  };

  const handleConfirm = async () => {
    setIsOpInProgress(true);

    try {
      const response = await chatService.updateChatTitle(
        chatId,
        inputValue.trim(),
        undefined,
      );

      if (response.success) {
        notifier.showSuccess("Chat title updated successfully.");
        onConfirmCallback(inputValue.trim());
        handleClose();
        return;
      }

      notifier.showError(renameFailureMessage(response));
    } catch {
      notifier.showError("Failed to update the chat title. Please try again.");
    } finally {
      setIsOpInProgress(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          fontSize: "19px",
        }}
      >
        Rename Chat
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box
          sx={{
            paddingTop: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.75,
            }}
          >
            Enter a new title for this chat:
          </Typography>

          <CoreInput
            fullWidth
            autoFocus
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter chat title"
            disabled={isOpInProgress}
            inputProps={{
              maxLength: 100,
            }}
            sx={{
              "& .MuiInputBase-root": {
                height: "48px",
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={isOpInProgress}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isOpInProgress}
          loading={isOpInProgress}
        >
          Rename
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameChatDialog;
